import { ArrowLeft, ChevronRight, Lock } from 'lucide-react'
import { EndpointHint, LoadFailure, Skeleton } from '../../core/index.js'
import { useShiftPreferences } from '../hooks.js'
import type { PastPreferencesMonth } from '../types.js'
import { describePeriod } from './preferencesText.js'
import { formatRunInstant } from './scheduleRunText.js'
import { ShiftPreferencesSummary } from './ShiftPreferencesPanel.js'

/** "Released Jan 19, 3:00 PM" or "Closed": why a month's preferences can no longer change. */
function lockedLine(month: Pick<PastPreferencesMonth, 'releasedAt'>): string {
  return month.releasedAt ? `Released ${formatRunInstant(month.releasedAt)}` : 'Closed'
}

/**
 * The worker's earlier months with preferences they can no longer change - their schedule released, or their
 * preferences closed - newest first, each opening what they asked for, read-only. Nothing when there are none.
 */
export function PastPreferencesCard({ months, onOpen }: { months: readonly PastPreferencesMonth[]; onOpen: (period: string) => void }) {
  if (months.length === 0) {
    return null
  }
  return (
    <div>
      <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid mb-1.5">Past preferences</div>
      <ul className="bg-white border border-marine/15 divide-y divide-marine/10">
        {months.map((month) => (
          <li key={month.period}>
            <button type="button" onClick={() => onOpen(month.period)} className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-marine/5">
              <span className="min-w-0">
                <span className="block text-xs font-extrabold text-ink">{describePeriod(month.period)}</span>
                <span className="flex items-center gap-1 text-[10px] text-ink-mid">
                  <Lock aria-hidden="true" className="h-3 w-3" />
                  {lockedLine(month)} · read only
                </span>
              </span>
              <ChevronRight aria-hidden="true" className="h-4 w-4 shrink-0 text-marine" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** One past month's preferences as the worker saved them, read-only: they can no longer be changed. */
export function PastPreferencesMonthView({ workerId, period, onDone }: { workerId: string; period: string; onDone: () => void }) {
  const query = useShiftPreferences(workerId, period)
  return (
    <div className="max-w-md mx-auto flex flex-col gap-3 p-4">
      <button type="button" onClick={onDone} className="inline-flex items-center gap-1 self-start text-[11px] font-bold text-marine hover:text-marine-dk">
        <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />
        My shifts
      </button>
      <section className="bg-white border border-marine/15 p-4">
        <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid">
          {describePeriod(period)}
          <EndpointHint className="ml-2">GET /workers/{'{workerId}'}/shift-preferences?period=</EndpointHint>
        </div>
        <h2 className="text-sm font-extrabold text-ink">What you asked for</h2>
        {query.isPending ? (
          <Skeleton className="mt-2" />
        ) : query.isError ? (
          <LoadFailure className="mt-2" message="Your preferences could not be loaded." onRetry={() => void query.refetch()} retrying={query.isFetching} />
        ) : (
          <>
            <p className="mt-0.5 mb-3 flex items-center gap-1 text-[11px] text-ink-mid">
              <Lock aria-hidden="true" className="h-3 w-3" />
              {lockedLine({ releasedAt: query.data.releasedAt ?? null })}. These can no longer be changed.
            </p>
            <ShiftPreferencesSummary preferences={query.data.preferences} period={period} />
          </>
        )}
      </section>
    </div>
  )
}
